// Dedicated /pricing page — the canonical, linkable pricing destination
// (the SaaS-standard pattern). It reuses the existing self-serve pricing
// cards (AgencyPricingPlans, with all its OAuth + checkout wiring intact),
// then adds a feature comparison table and a pricing FAQ beneath them.
//
// The homepage keeps only a compact teaser (PricingTeaserBand) that links
// here, so the full detail lives in exactly one place.
//
// Because the Enterprise "Contact us" card and the FAQ "Talk to us" link
// fire the openEmbedInquiry() window event, this page mounts its own
// <EmbedInquiryModal> + listener (the same pattern the landing uses).

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import ToolFunnelHeader from '@/components/tools/ToolFunnelHeader';
import Footer from '@/components/Footer';
import { AgencyPricingPlans } from '@/components/lead-gen-website/AgencyPricingPlans';
import { PricingFaq } from '@/components/lead-gen-website/PricingFaq';
import { EmbedInquiryModal } from '@/components/lead-gen-website/EmbedInquiryModal';
import { openEmbedInquiry } from '@/components/lead-gen-website/openEmbedInquiry';
import { FloatingAccountPill } from '@/components/auth/FloatingAccountPill';

// Factual reassurance chips for the hero. Every line is sourced from the
// real plan terms (Free needs no card, month-to-month cancellation, INR
// billing with a GST invoice, top-up credits never expire) — no invented
// social proof.
const HERO_CHIPS = [
  'No card to start',
  'Cancel anytime',
  'USD or INR (GST invoice)',
  'Top-up credits never expire',
];

export default function Pricing() {
  // Embed-inquiry modal, lifted to the page root so the Enterprise card and
  // the FAQ "Talk to us" link can open it from anywhere via openEmbedInquiry().
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquirySource, setInquirySource] = useState('pricing');
  const [inquiryVariant, setInquiryVariant] = useState<'embed' | 'talk'>('talk');

  useEffect(() => {
    const onOpen = (e: Event) => {
      const ce = e as CustomEvent<{ source?: string; variant?: 'embed' | 'talk' }>;
      setInquirySource(ce.detail?.source || 'pricing');
      setInquiryVariant(ce.detail?.variant || 'talk');
      setInquiryOpen(true);
    };
    window.addEventListener('open-embed-inquiry', onOpen);
    return () => window.removeEventListener('open-embed-inquiry', onOpen);
  }, []);

  return (
    <div className="bg-gray-950 text-white antialiased">
      <Helmet>
        <title>Pricing | Fly Agency Tools</title>
        <meta
          name="description"
          content="Simple, transparent pricing for agencies. Start with a 7-day free trial (full Starter access, no card), then scale with plans from $9/mo. Pay only for the credits you consume, cancel anytime."
        />
        <link rel="canonical" href="https://fly-social.com/pricing" />
        <meta property="og:title" content="Pricing | Fly Agency Tools" />
        <meta
          property="og:description"
          content="Simple, transparent pricing for agencies. Start with a 7-day free trial (full Starter access, no card), then scale with plans from $9/mo. Pay only for the credits you consume, cancel anytime."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fly-social.com/pricing" />
      </Helmet>

      {/* Stripped funnel header (logo + single CTA), identical to the agency
          homepage and the tool funnel pages. We intentionally do NOT use the
          corporate SiteNavBar here so /pricing stays inside the focused funnel. */}
      <ToolFunnelHeader />

      {/* Floating account pill — bottom-right, only renders for signed-in
          users WITH an active trial / paid plan / lifetime. First-time
          signed-in users (no agency) and signed-out visitors see nothing. */}
      <FloatingAccountPill context="pricing" />

      {/* Hero — the single page header (AgencyPricingPlans below renders with
          showHeader=false so there is no duplicate heading). Extra top padding
          clears the fixed funnel logo. Ambient violet/fuchsia glow gives the
          dark brand some cinematic depth without animation. */}
      <header className="relative overflow-hidden bg-gray-950 px-6 pt-36 pb-10 text-center lg:px-8 lg:pt-44 lg:pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(139,92,246,0.22), transparent 60%), radial-gradient(ellipse 50% 40% at 80% 20%, rgba(236,72,153,0.12), transparent 55%)',
          }}
        />
        <div className="relative mx-auto max-w-3xl">
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-400">
            Pricing
          </div>
          <h1
            className="font-bold tracking-tight text-white"
            style={{ fontSize: 'clamp(2.4rem, 5vw, 4rem)', lineHeight: 1.05 }}
          >
            Simple, transparent pricing.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/60">
            Start with a 7-day free trial (full Starter access, no card required),
            then scale as your lead flow grows. Pay only for the credits you consume,
            and cancel anytime.
          </p>

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5">
            {HERO_CHIPS.map((chip) => (
              <li
                key={chip}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-sm text-white/75"
              >
                <Check className="size-3.5 shrink-0 text-emerald-400" />
                {chip}
              </li>
            ))}
          </ul>
        </div>
      </header>

      {/* Product Hunt launch-offer banner. Points pricing visitors to the
          better launch deal running on /producthunt (a higher plan for a
          lower plan's price, first month free). Kept tasteful and on-brand
          (the same violet/fuchsia the PH page uses) so it reads as a premium
          callout, not a popup ad. Whole banner is the click target. */}
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <Link
          to="/producthunt"
          className="group relative block overflow-hidden rounded-2xl border border-fuchsia-400/30 bg-gradient-to-r from-violet-600/20 via-fuchsia-600/15 to-violet-600/20 px-5 py-4 transition hover:border-fuchsia-400/55 sm:px-7 sm:py-5"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background:
                'radial-gradient(ellipse 45% 90% at 0% 50%, rgba(236,72,153,0.20), transparent 60%)',
            }}
          />
          <div className="relative flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="flex items-center gap-3.5">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/30">
                <Sparkles className="size-5 text-white" />
              </span>
              <div>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <span className="rounded-full bg-fuchsia-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-200">
                    Launch week
                  </span>
                  <span className="text-sm font-semibold text-white sm:text-base">
                    Our Product Hunt offer is live
                  </span>
                </div>
                <p className="mt-1 text-sm text-white/65">
                  Get a higher plan for the price of a lower one. First month free, this week only.
                </p>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm transition group-hover:bg-white/90">
              See the deal
              <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
            </span>
          </div>
        </Link>
      </div>

      <main>
        <AgencyPricingPlans showHeader={false} showTrialCta={false} />
        <PricingFaq />

        {/* Closing CTA — the final conversion step the pricing pattern calls
            for. Reuses the existing inquiry modal for "talk to us"; the
            secondary action jumps back to the plan cards (id="pricing"). */}
        <section className="relative overflow-hidden bg-gray-950 py-24 text-center lg:py-28">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 55% 50% at 50% 100%, rgba(139,92,246,0.18), transparent 60%)',
            }}
          />
          <div className="relative mx-auto max-w-2xl px-6 lg:px-8">
            <h2
              className="font-bold tracking-tight text-white"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.1 }}
            >
              Not sure which plan fits?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-white/60">
              Tell us about your agency and we'll point you to the right tier.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => openEmbedInquiry('pricing-final-cta', 'talk')}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-105"
              >
                Talk to our team
                <ArrowRight className="size-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById('pricing')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Compare plans
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer compact />

      <EmbedInquiryModal
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        source={inquirySource}
        title={inquiryVariant === 'talk' ? 'Talk to our team' : 'Get your embed code'}
        subtitle={
          inquiryVariant === 'talk'
            ? "Tell us about your agency and we'll set up a quick call this week."
            : "Tell us about your agency and we'll send your white-labeled embed within 24 hours."
        }
        ctaLabel={inquiryVariant === 'talk' ? 'Request a call' : 'Get my embed code'}
      />
    </div>
  );
}
